// Yuzenya — three-experience narrative (EN / NL / NO)
export const translations = {
  en: {
    nav: {
      vision: "Manifesto",
      experience: "Experiences",
      journey: "Arrival",
      location: "Location",
      days: "Days",
      investors: "Investors",
      waitlist: "Waitlist",
      menu: "Menu",
    },
    hero: {
      eyebrow: "A future retreat in Norway",
      badge: "Now in development · Norway · Opening envisioned 2028",
      title: "Come home lighter\nthan you arrived.",
      subtitle:
        "Taking shape beside a quiet lake in Norway, Yuzenya is an intimate retreat devoted to a quieter mind, a stronger body and a more considered way of living. Guided practice, seasonal nourishment and daily life outdoors come together in one carefully shaped experience. We are working towards welcoming our first guests in 2028.",
      cta_primary: "Join the Waitlist",
      cta_secondary: "Investor Information",
      scroll: "Scroll",
      tagline: "Where nature slows you down. And life begins again.",
    },
    vision: {
      eyebrow: "The Yuzenya Manifesto",
      title: "Nothing here is incidental.",
      body_1:
        "Yuzenya begins with a simple belief: true luxury is the feeling that everything has been considered. The pace. The light. The welcome. The silence between one experience and the next.",
      body_2:
        "Nature sets the rhythm, hospitality remains quietly present, and technology supports without asking for attention. The result is not an escape from life, but a clearer way back to it.",
      stat_a_value: "1.5h",
      stat_a_label: "North of Oslo",
      stat_b_value: "Lakeside",
      stat_b_label: "Pine forest setting",
      stat_c_value: "2028",
      stat_c_label: "Opening envisioned",
    },
    pillars: {
      eyebrow: "The Foundations",
      title: "Three Transformative Experiences",
      items: [
        {
          n: "01",
          title: "Retreat",
          statement: "Where your mind slows down.",
          body: "Guided days with room for stillness, movement and reflection. Nothing is forced; everything is an invitation.",
        },
        {
          n: "02",
          title: "Nourishment",
          statement: "Where food becomes transformation.",
          body: "Seasonal ingredients, fermentation, fire and craft, shared with the knowledge to continue at home.",
        },
        {
          n: "03",
          title: "Nature",
          statement: "Where the wild restores your body.",
          body: "Forest, water, sauna, cold and changing daylight become part of the natural rhythm of every stay.",
        },
      ],
    },
    arrival: {
      eyebrow: "The Threshold",
      title: "The journey begins before you arrive.",
      body_1:
        "Our ambition is for the Yuzenya experience to begin the moment guests leave Oslo Airport Gardermoen. As the road moves deeper into the Norwegian landscape, everyday noise gradually gives way to forest, water and anticipation. A carefully considered transfer makes space to arrive slowly, without friction.",
      body_2:
        "The final approach is a sensory threshold: the scent of wood and native herbs, a shift in sound, warm light through the trees and a passage from shadow into openness. The gate is not simply an entrance. It marks the moment the outside world is left behind.",
      body_3: "Arrival at Yuzenya is the first ritual.",
    },
    location: {
      eyebrow: "Grue · Norway",
      title: "Close enough to reach.\nFar enough to transform.",
      body_1:
        "Yuzenya is planned for a lakeside setting in Grue, near Kirkenær, surrounded by pine forest, open water and expansive Nordic skies.",
      body_2:
        "Approximately 100 kilometres from Oslo Airport Gardermoen, the location offers meaningful seclusion without becoming inaccessible. The journey takes around 90 minutes by road, depending on route and season. Curated transfers and courtesy cars are being explored as part of a seamless arrival experience.",
      point_a: "Oslo Airport Gardermoen",
      point_b: "Yuzenya · Kirkenær",
      distance: "Approx. 100 km",
      duration: "Around 90 minutes by car, depending on route and season",
    },
    stories: {
      eyebrow: "The Experience",
      title: "A Day at Yuzenya",
      intro_1:
        "There is structure at Yuzenya, but never pressure. Each day follows the rhythm of light, appetite, movement and rest. Guests may join guided practices, spend time alone or simply follow what their body asks for.",
      intro_2: "",
      items: [
        {
          tag: "Morning",
          title: "The day begins before it asks anything of you.",
          statement: "",
          alt: "Morning mist moving across a Norwegian lake beside the retreat dock.",
          body:
            "Step onto the dock as morning mist moves across the water. Breathe, move or enter the cold before warming slowly in the sauna. Breakfast may be shared in silence, giving the senses time to awaken before conversation returns.",
        },
        {
          tag: "Day",
          title: "Practice, nourishment and landscape move together.",
          statement: "",
          alt: "Seasonal retreat meal prepared with local ingredients and fermentation techniques.",
          body:
            "Guided sessions create space for reflection, learning and movement. Between them, the forest, lake and cabins offer room to walk, read, rest or be alone. Meals are shaped by the season: ingredients gathered locally, transformed through fermentation, fire and patient craft. The kitchen is not hidden from the experience. It is part of it.",
        },
        {
          tag: "Evening",
          title: "The body settles before the mind follows.",
          statement: "",
          alt: "Forest, sauna and open sky at dusk during a Nordic retreat evening.",
          body:
            "As daylight softens, guests return to warmth. Dinner, quiet conversation, a fire outdoors or a final walk beneath the open sky bring the day to a natural close. Nothing needs to be completed. Nothing needs to be achieved.",
        },
      ],
    },
    reset: {
      eyebrow: "Beyond the Retreat",
      title: "Take the feeling home.",
      body:
        "A retreat only matters if something of it remains. Yuzenya helps guests understand the practices behind the experience. Sessions may explore sleep, stress, breath, daylight, movement, thermal recovery, fermentation and nourishing food in ways that can continue at home.\n\nOptional personal insights can make recovery more tangible, without turning the retreat into a laboratory or making data the destination.",
      closing:
        "We do not offer an escape from life. We reveal how beautiful life can feel when everything unnecessary is removed.",
    },
    thoughtful: {
      eyebrow: "Thoughtful by Design",
      title: "Built with the land, not placed upon it.",
      body_1:
        "Our aim is to work with what is already there. Architecture will follow the contours, trees and views of the site. Materials found on the land will be reused wherever possible, finding new purpose in buildings, pathways and the surrounding landscape.",
      body_2:
        "Renewable energy, responsible heating and discreet technology will support year-round comfort without demanding attention.",
      body_3:
        "Sustainability is not a story added afterwards. It is part of every design decision.",
    },
    investor: {
      eyebrow: "Investment Opportunity",
      title: "A considered model for meaningful hospitality.",
      body_1:
        "Yuzenya is being developed as an intimate, programme-led destination rather than a conventional hotel. Value is created through a limited guest experience, carefully curated retreat programmes, destination nourishment and an arrival-to-departure journey designed to be remembered and repeated.",
      body_2:
        "The concept sits at the intersection of wellbeing, longevity, nature, food, sustainable design and growing interest in restorative travel within the Nordics.",
      body_3:
        "We are exploring conversations with aligned investors who share our ambition to create a new standard for thoughtful Nordic hospitality.",
      name: "Full name",
      email: "Email address",
      interest: "Your interest in Yuzenya",
      cta: "Request Investor Information",
      success: "Thank you. We will be in touch personally.",
    },
    waitlist: {
      eyebrow: "Follow the Journey",
      title: "Be there as Yuzenya takes shape.",
      body:
        "Receive occasional updates from the land, the design process and the development of our retreat programmes, and be among the first to hear when the first stays are announced.",
      first_name: "First name",
      email: "Email address",
      cta: "Join the Waitlist",
      success: "You're on the list. We'll write when there's something worth saying.",
    },
    closing: {
      quote: "Sometimes the greatest luxury is simply having space to breathe.",
      attribution: "Yuzenya",
    },
    footer: {
      tagline: "Where nature slows you down. And life begins again.",
      description:
        "Yuzenya is a future Nordic retreat in development near Kirkenær, Norway, bringing together guided retreats, seasonal nourishment and restorative time in nature.",
      location: "Near Kirkenær, approx. 100 km from Oslo Airport",
      contact: "Contact",
      rights: "All rights reserved.",
      development: "Yuzenya is a future destination currently in development.",
    },
    errors: {
      generic: "Something went wrong. Please try again.",
      email: "Please enter a valid email address.",
    },
  },

  nl: {
    nav: {
      vision: "Manifest",
      experience: "Ervaringen",
      journey: "Aankomst",
      location: "Locatie",
      days: "Dagen",
      investors: "Investeerders",
      waitlist: "Wachtlijst",
      menu: "Menu",
    },
    hero: {
      eyebrow: "Een toekomstige retreat, Noorwegen",
      badge: "Nu in ontwikkeling · Noorwegen · Openingsvisie 2028",
      title: "Kom lichter thuis\ndan je vertrok.",
      subtitle:
        "Ontworpen aan een Noors meer, wordt Yuzenya een toekomstige Nordic retreat waar radicale rust, transformatieve voeding en wetenschappelijk onderbouwde onderdompeling in de natuur samenkomen. We werken toe naar het verwelkomen van onze eerste gasten in 2028.",
      cta_primary: "Schrijf je in op de wachtlijst",
      cta_secondary: "Investeerdersinformatie",
      scroll: "Scroll",
      tagline: "Waar de natuur je tempo verlaagt. En het leven opnieuw begint.",
    },
    vision: {
      eyebrow: "Het Yuzenya Manifest",
      title: "Echte luxe is geen overdaad.\nHet is intentie.",
      body_1:
        "Bij Yuzenya heeft elk detail een doel. De natuur leidt, technologie ondersteunt stil, en gastvrijheid is aanwezig zonder op te dringen.",
      body_2:
        "Onze ambitie is om verwachtingen te overtreffen, niet door pracht, maar door zorgvuldigheid, buitengewone kwaliteit en oprechte aandacht. We creëren de voorwaarden voor een rustigere geest, een gezonder lichaam en een hernieuwde waardering voor hoe mooi het leven kan voelen wanneer alles overbodigs wegvalt.",
      stat_a_value: "1.5u",
      stat_a_label: "Ten noorden van Oslo",
      stat_b_value: "Aan het water",
      stat_b_label: "In het dennenbos",
      stat_c_value: "2028",
      stat_c_label: "Openingsvisie",
    },
    pillars: {
      eyebrow: "De Fundamenten",
      title: "Drie Transformatieve Ervaringen",
      items: [
        {
          n: "01",
          title: "Retreat",
          statement: "Waar je geest tot rust komt.",
          body: "Radicale rust, doordachte begeleiding en ruimte om weer verbinding te maken, nooit opgelegd, altijd uitnodigend.",
        },
        {
          n: "02",
          title: "Nourishment",
          statement: "Waar voeding transformatie wordt.",
          body: "Seizoensgebonden, lokaal en levend: eten als vakmanschap, ontdekking en een essentieel onderdeel van vernieuwing.",
        },
        {
          n: "03",
          title: "Nature",
          statement: "Waar de wildernis je lichaam herstelt.",
          body: "Noors friluftsliv, thermisch herstel en buitenrituelen, gevoed door moderne wetenschap.",
        },
      ],
    },
    arrival: {
      eyebrow: "De Overgang",
      title: "De reis begint voordat je aankomt",
      body_1:
        "We stellen ons voor dat de Yuzenya-reis begint op het moment dat gasten Oslo Gardermoen verlaten. In de rustige comfort van een zorgvuldig samengesteld transfer maakt het rumoer van het dagelijks leven geleidelijk plaats voor bos, water en verwachting.",
      body_2:
        "De laatste nadering wordt een zintuiglijke overgang: een symbolische poort, een warme kaarslichtige doorgang, subtiele tonen van dennen, ceder, jeneverbes en citrus, en zachte Scandinavische ritmes die gasten naar een ander levenstempo trekken. Dan opent het landschap zich, en onthult water, bos en lucht.",
      body_3: "Aankomst bij Yuzenya is niet simpelweg inchecken. Het is het eerste ritueel.",
    },
    location: {
      eyebrow: "Locatie",
      title: "Dichtbij genoeg om te bereiken.\nVer genoeg om te transformeren.",
      body_1:
        "Yuzenya is voorzien op een unieke locatie aan een meer nabij Kirkenær, Noorwegen, omgeven door bos, open luchten en ononderbroken uitzicht over het water.",
      body_2:
        "Op ongeveer 100 kilometer van Oslo Gardermoen Airport voelt de retreat diep verwijderd van het dagelijks leven, terwijl hij toegankelijk blijft voor internationale gasten. Premium transfers en courtesy cars worden onderzocht als onderdeel van de volledige aankomstervaring.",
      point_a: "Oslo Airport Gardermoen",
      point_b: "Yuzenya · Kirkenær",
      distance: "Ca. 100 km",
      duration: "Ongeveer 90 minuten met de auto, afhankelijk van route en seizoen",
    },
    stories: {
      eyebrow: "De ervaring",
      title: "Dagen bij Yuzenya",
      intro_1:
        "Een dag bij Yuzenya begint met licht dat over het water beweegt, de geur van het bos en de vrijheid om een natuurlijk ritme te volgen. Er is structuur, maar nooit druk. Beweging, stilte, voeding, leren en herstel vloeien in elkaar over.",
      intro_2: "Elk verblijf wordt gevormd door drie transformatieve ervaringen.",
      items: [
        {
          tag: "01",
          title: "Retreat",
          statement: "Waar je geest tot rust komt.",
          body:
            "Bij Yuzenya is retreat geen schema om af te werken, maar een ritme om binnen te gaan. Stilte wordt verwelkomd, tijd mag vertragen en elke praktijk wordt zonder druk aangeboden.\n\nBegeleide sessies, discrete gastvrijheid en ruimtes gevormd voor reflectie creëren de voorwaarden voor een rustigere geest en een diepere verbinding met jezelf. Het programma biedt richting zonder verplichting, zodat elke gast de balans vindt tussen deelname, solitude en rust.",
        },
        {
          tag: "02",
          title: "Nourishment",
          statement: "Waar voeding transformatie wordt.",
          body:
            "Bij Yuzenya is voeding geen faciliteit. Het is een essentieel onderdeel van de retreat-ervaring en een pad naar grotere vitaliteit.\n\nSeizoensingrediënten, lokale foerage, fermentatie, vuur en vakmanschap komen samen in gerechten ontworpen om te voeden, te verrassen en te leren. Levende culturen, zuurdesem, koji, miso, kefir en geconserveerde Noordse ingrediënten worden verkend, niet als trends, maar als deel van een diepere relatie tussen voeding, gezondheid en plek.\n\nDoor open keukens, workshops en gedeelde rituelen ontdekken gasten niet alleen een andere manier van eten, maar kennis en praktijken die ze mee naar huis kunnen nemen.",
        },
        {
          tag: "03",
          title: "Nature",
          statement: "Waar de wildernis je lichaam herstelt.",
          body:
            "Het Noorse landschap is onze grootste leraar. Sauna, koud water, bossen, frisse lucht en veranderend licht worden in het dagelijks leven verweven door ademwerk, wandelen, kajakken, blote-voetenpaden en stille buitenrituelen.\n\nOude Noordse praktijken ontmoeten modern welzijnsonderzoek en helpen gasten de herstellende effecten van de natuur te ervaren op manieren die zowel intuïtief als tastbaar zijn. Warmte, kou, beweging, daglicht en slaap worden onderdeel van een compleet ecosysteem voor herstel in de openlucht, in plaats van geïsoleerde activiteiten.",
        },
      ],
    },
    reset: {
      eyebrow: "Meer dan een reset",
      title: "Vertrek hersteld. Kom geïnformeerd terug.",
      body:
        "Yuzenya is ontworpen om gasten niet alleen anders te laten voelen, maar hen ook te helpen begrijpen waarom. Retreat-programma's verkennen slaap, stress, ademhaling, licht, beweging, thermisch herstel, fermentatie en voedzame voeding via praktische ervaringen die thuis voortgezet kunnen worden.\n\nWaar passend kunnen gasten ervoor kiezen inzicht te krijgen in indicatoren zoals slaapkwaliteit, HRV en rusthartslag, niet om de retreat in een laboratorium te veranderen, maar om herstel tastbaarder te maken.",
      closing:
        "We bieden geen ontsnapping aan het leven. We laten zien hoe mooi het leven kan voelen wanneer al het overbodige is verwijderd.",
    },
    thoughtful: {
      eyebrow: "Doordacht ontwerp",
      title: "Doordacht ontwerp",
      body_1:
        "Dezelfde filosofie geeft vorm aan de plek zelf. Architectuur volgt het land in plaats van ermee te concurreren. Materialen uit het terrein zijn bedoeld om binnen het circulaire systeem te blijven en nieuwe bestemming te vinden in gebouwen, paden en landschap.",
      body_2:
        "Hernieuwbare energie, verantwoorde verwarming en discrete technologie zullen comfort ondersteunen zonder aandacht te vragen.",
      body_3:
        "Elke investering, van een cabin tot een deurklink, wordt afgezet tegen één vraag: verdiept het Retreat, Nourishment of Nature, en helpt het ons de verwachtingen van de gast te overtreffen?",
    },
    investor: {
      eyebrow: "Investeringsmogelijkheid",
      title: "Gebouwd voor betekenis.\nOntworpen voor waarde op lange termijn.",
      body_1:
        "Yuzenya is momenteel in ontwikkeling, met de ambitie om zijn eerste gasten in 2028 te verwelkomen. Het concept wordt gevormd op het snijvlak van wellness-toerisme, Longevity, experiëntiële gastvrijheid, duurzaam ontwerp en de groeiende vraag naar betekenisvol reizen in de Nordics.",
      body_2:
        "Dit is geen concept gebouwd rond meer kamers. Het is gebouwd rond diepere waarde per gast: een onderscheidend retreat-programma, transformatieve voeding, gedenkwaardige buitenrituelen en een ervaring die gasten lang na thuiskomst kunnen voortzetten.",
      body_3:
        "We onderzoeken partnerschappen met investeerders die onze ambitie delen om een nieuwe standaard voor doordachte Nordic gastvrijheid te creëren.",
      name: "Volledige naam",
      email: "E-mailadres",
      interest: "Jouw interesse in Yuzenya",
      cta: "Vraag investeerdersinformatie aan",
      success: "Dank je. We nemen persoonlijk contact op.",
    },
    waitlist: {
      eyebrow: "De Founding Waitlist",
      title: "Behoor tot de eersten\ndie Yuzenya ervaren.",
      body:
        "Volg de creatie van Yuzenya, ontvang updates van achter de schermen en hoor als eerste wanneer toekomstige retreat-data beschikbaar komen.",
      first_name: "Voornaam",
      email: "E-mailadres",
      cta: "Schrijf je in op de Founding Waitlist",
      success: "Je staat op de lijst. We schrijven wanneer er iets waardevols te zeggen is.",
    },
    closing: {
      quote: "Soms is de grootste luxe simpelweg ruimte hebben om te ademen.",
      attribution: "Yuzenya",
    },
    footer: {
      tagline: "Waar de natuur je tempo verlaagt. En het leven opnieuw begint.",
      location: "Nabij Kirkenær, ca. 100 km van Oslo Airport",
      contact: "Contact",
      rights: "Alle rechten voorbehouden.",
      development: "Yuzenya is een toekomstige bestemming in ontwikkeling.",
    },
    errors: {
      generic: "Er ging iets mis. Probeer het opnieuw.",
      email: "Voer een geldig e-mailadres in.",
    },
  },

  no: {
    nav: {
      vision: "Manifest",
      experience: "Opplevelser",
      journey: "Ankomst",
      location: "Beliggenhet",
      days: "Dager",
      investors: "Investorer",
      waitlist: "Venteliste",
      menu: "Meny",
    },
    hero: {
      eyebrow: "En fremtidig retreat, Norge",
      badge: "Nå under utvikling · Norge · Åpning planlagt 2028",
      title: "Kom hjem lettere\nenn du dro.",
      subtitle:
        "Ved en norsk innsjø tar Yuzenya form, en fremtidig nordisk retreat der radikal ro, transformerende næring og forskningsbasert fordypning i naturen møtes. Vi arbeider mot å ønske våre første gjester velkommen i 2028.",
      cta_primary: "Meld deg på ventelisten",
      cta_secondary: "Investorinformasjon",
      scroll: "Rull",
      tagline: "Der naturen senker tempoet. Og livet begynner på nytt.",
    },
    vision: {
      eyebrow: "Yuzenya-manifestet",
      title: "Ekte luksus er ikke overflod.\nDet er intensjon.",
      body_1:
        "På Yuzenya har hver detalj en hensikt. Naturen leder, teknologien støtter stille, og gjestfriheten er til stede uten å påtvinge seg.",
      body_2:
        "Vår ambisjon er å overgå forventninger, ikke gjennom overdådighet, men gjennom omtanke, enestående kvalitet og oppriktig omsorg. Vi skaper forholdene for et roligere sinn, en sunnere kropp og en fornyet takknemlighet for hvor vakkert livet kan føles når alt unødvendig faller bort.",
      stat_a_value: "1.5t",
      stat_a_label: "Nord for Oslo",
      stat_b_value: "Ved vannet",
      stat_b_label: "I furuskogen",
      stat_c_value: "2028",
      stat_c_label: "Planlagt åpning",
    },
    pillars: {
      eyebrow: "Fundamentene",
      title: "Tre transformerende opplevelser",
      items: [
        {
          n: "01",
          title: "Retreat",
          statement: "Der sinnet ditt bremser ned.",
          body: "Radikal ro, gjennomtenkt veiledning og rom til å gjenknytte, aldri påtvunget, alltid innbydende.",
        },
        {
          n: "02",
          title: "Nourishment",
          statement: "Der maten blir transformasjon.",
          body: "Sesongbasert, lokal og levende: mat som håndverk, oppdagelse og en vesentlig del av fornyelsen.",
        },
        {
          n: "03",
          title: "Nature",
          statement: "Der villmarken gjenoppretter kroppen.",
          body: "Norsk friluftsliv, termisk restitusjon og utendørs ritualer inspirert av moderne vitenskap.",
        },
      ],
    },
    arrival: {
      eyebrow: "Terskelen",
      title: "Reisen begynner før du ankommer",
      body_1:
        "Vi ser for oss at Yuzenya-reisen begynner i det gjestene forlater Oslo Gardermoen. I den rolige komforten av en kuratert transfer gir hverdagens støy gradvis plass til skog, vann og forventning.",
      body_2:
        "Den siste tilnærmingen blir en sanselig overgang: en symbolsk port, en varm passasje opplyst av stearinlys, subtile toner av furu, sedertre, einer og sitrus, og lave skandinaviske rytmer som fører gjestene inn i et annet livstempo. Så åpner landskapet seg, og avslører vann, skog og himmel.",
      body_3: "Ankomst til Yuzenya er ikke bare innsjekk. Det er det første ritualet.",
    },
    location: {
      eyebrow: "Beliggenhet",
      title: "Nær nok til å nå.\nLangt nok til å transformere.",
      body_1:
        "Yuzenya er tenkt lagt til en unik eiendom ved vannet nær Kirkenær, Norge, omgitt av skog, åpen himmel og uavbrutt utsikt over vannet.",
      body_2:
        "Cirka 100 kilometer fra Oslo Lufthavn Gardermoen vil retreat kjennes dypt fjernt fra dagliglivet, mens den forblir tilgjengelig for internasjonale gjester. Premium transfers og courtesy-biler utforskes som del av den komplette ankomstopplevelsen.",
      point_a: "Oslo Lufthavn Gardermoen",
      point_b: "Yuzenya · Kirkenær",
      distance: "Ca. 100 km",
      duration: "Rundt 90 minutter med bil, avhengig av rute og årstid",
    },
    stories: {
      eyebrow: "Opplevelsen",
      title: "Dager på Yuzenya",
      intro_1:
        "En dag på Yuzenya begynner med lys som beveger seg over vannet, duften av skogen og friheten til å følge en rytme som føles naturlig. Det er struktur, men aldri press. Bevegelse, stillhet, næring, læring og restitusjon flyter over i hverandre.",
      intro_2: "Hvert opphold formes av tre transformerende opplevelser.",
      items: [
        {
          tag: "01",
          title: "Retreat",
          statement: "Der sinnet ditt bremser ned.",
          body:
            "På Yuzenya er retreat ikke en plan som skal fullføres, men en rytme å tre inn i. Stillhet ønskes velkommen, tiden får lov til å bremse ned, og hver praksis tilbys uten press.\n\nGuidede økter, diskré gjestfrihet og rom formet for refleksjon skaper forholdene for et roligere sinn og en dypere forbindelse med deg selv. Programmet gir retning uten forpliktelse, slik at hver gjest kan finne balansen mellom deltakelse, ensomhet og hvile.",
        },
        {
          tag: "02",
          title: "Nourishment",
          statement: "Der maten blir transformasjon.",
          body:
            "På Yuzenya er ikke maten en fasilitet. Den er en vesentlig del av retreat-opplevelsen og en vei mot større vitalitet.\n\nSesongens råvarer, lokal sanking, fermentering, ild og håndverk møtes i måltider utformet for å nære, overraske og lære. Levende kulturer, surdeig, koji, miso, kefir og konserverte nordiske ingredienser utforskes ikke som trender, men som del av et dypere forhold mellom mat, helse og sted.\n\nGjennom åpne kjøkken, workshops og delte ritualer oppdager gjestene ikke bare en annen måte å spise på, men kunnskap og praksiser de kan ta med seg hjem.",
        },
        {
          tag: "03",
          title: "Nature",
          statement: "Der villmarken gjenoppretter kroppen.",
          body:
            "Det norske landskapet er vår største læremester. Badstue, kaldt vann, skoger, frisk luft og skiftende lys veves inn i hverdagen gjennom pustearbeid, gange, kajakk, barfotstier og stille utendørs ritualer.\n\nGamle nordiske praksiser møter moderne velværeforskning og hjelper gjestene å erfare naturens gjenopprettende virkning på måter som føles både intuitive og håndgripelige. Varme, kulde, bevegelse, dagslys og søvn blir del av et helhetlig utendørs restitusjons-økosystem, ikke isolerte aktiviteter.",
        },
      ],
    },
    reset: {
      eyebrow: "Mer enn en reset",
      title: "Reis restituert. Kom informert tilbake.",
      body:
        "Yuzenya er utformet ikke bare for å hjelpe gjestene å føle seg annerledes, men også for å hjelpe dem å forstå hvorfor. Retreat-programmene utforsker søvn, stress, pust, lys, bevegelse, termisk restitusjon, fermentering og næringsrik mat gjennom praktiske erfaringer som kan videreføres hjemme.\n\nDer det er passende, kan gjestene velge å få innsikt i indikatorer som søvnkvalitet, HRV og hvilepuls, ikke for å gjøre retreat om til et laboratorium, men for å gjøre restitusjon mer håndgripelig.",
      closing:
        "Vi tilbyr ingen flukt fra livet. Vi viser hvor vakkert livet kan føles når alt unødvendig er fjernet.",
    },
    thoughtful: {
      eyebrow: "Gjennomtenkt design",
      title: "Gjennomtenkt design",
      body_1:
        "Den samme filosofien former stedet selv. Arkitekturen følger landet fremfor å konkurrere med det. Materialer tatt fra tomten er ment å forbli i dens sirkulære system, og finne ny hensikt i bygninger, stier og landskap.",
      body_2:
        "Fornybar energi, ansvarlig oppvarming og diskré teknologi vil støtte komfort uten å kreve oppmerksomhet.",
      body_3:
        "Hver investering, fra en hytte til et dørhåndtak, måles mot ett spørsmål: fordyper det Retreat, Nourishment eller Nature, og hjelper det oss å overgå gjestens forventninger?",
    },
    investor: {
      eyebrow: "Investeringsmulighet",
      title: "Bygget for mening.\nUtformet for langsiktig verdi.",
      body_1:
        "Yuzenya er for tiden under utvikling, med ambisjonen om å ønske sine første gjester velkommen i 2028. Konseptet formes i skjæringspunktet mellom velværeturisme, Longevity, opplevelsesbasert gjestfrihet, bærekraftig design og økende etterspørsel etter meningsfulle reiser i Norden.",
      body_2:
        "Dette er ikke et konsept bygget rundt flere rom. Det er bygget rundt dypere verdi per gjest: et særegent retreat-program, transformerende næring, minneverdige utendørs ritualer og en opplevelse gjestene kan videreføre lenge etter hjemkomst.",
      body_3:
        "Vi utforsker partnerskap med investorer som deler vår ambisjon om å skape en ny standard for gjennomtenkt nordisk gjestfrihet.",
      name: "Fullt navn",
      email: "E-postadresse",
      interest: "Din interesse for Yuzenya",
      cta: "Be om investorinformasjon",
      success: "Takk. Vi tar personlig kontakt.",
    },
    waitlist: {
      eyebrow: "Founding Waitlist",
      title: "Vær blant de første\ntil å oppleve Yuzenya.",
      body:
        "Følg skapelsen av Yuzenya, motta oppdateringer fra kulissene og vær den første til å høre når fremtidige retreat-datoer blir tilgjengelige.",
      first_name: "Fornavn",
      email: "E-postadresse",
      cta: "Meld deg på Founding Waitlist",
      success: "Du er på listen. Vi skriver når det er noe verdt å si.",
    },
    closing: {
      quote: "Noen ganger er den største luksusen å ha rom til å puste.",
      attribution: "Yuzenya",
    },
    footer: {
      tagline: "Der naturen senker tempoet. Og livet begynner på nytt.",
      location: "Nær Kirkenær, ca. 100 km fra Oslo lufthavn",
      contact: "Kontakt",
      rights: "Alle rettigheter forbeholdt.",
      development: "Yuzenya er et fremtidig reisemål under utvikling.",
    },
    errors: {
      generic: "Noe gikk galt. Prøv igjen.",
      email: "Vennligst skriv inn en gyldig e-postadresse.",
    },
  },
};

export const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "nl", label: "NL" },
  { code: "no", label: "NO" },
];
